import { Button, DataTable, IconButton, Page } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import FileSaver from 'file-saver';
import { ampli } from 'lib-shared-tracking';

import { conversationsApi } from 'apis/conversations';
import { userContentApi } from 'apis/userContent';
import { appActions } from 'app/app.actions';
import { AppLink } from 'components/appLink';
import { channelAttributes } from 'utils/channels';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { ScheduledConversationsBanner } from '../banner/scheduledConversationsBanner';
import { ScheduledConversationsModal } from '../modal/scheduledConversationsModal';

export const ScheduledConversationsTable = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const { batches, isLoading, isSuccess, isError, refetch } =
    conversationsApi.useReadScheduledConversationBatchesQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        batches: data?.data.batches ?? [],
      }),
    });

  const [readScheduledConversationsCsv] =
    userContentApi.useLazyReadScheduledConversationsCsvQuery();

  const handleDownload = async (path: string): Promise<void> => {
    const text = await readScheduledConversationsCsv({ path }).unwrap();
    const file = new File([text], `${path.split('/').pop()}.csv`, {
      type: 'text/csv;charset=utf-8',
    });
    FileSaver.saveAs(file);
  };

  const handleImport = (): void => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'settings:scheduledConversations',
        },
      }),
    );

    ampli.scheduledConversationsCsvImportClicked();
  };

  const table = (
    <DataTable
      data={batches}
      columns={[
        {
          label: 'Schedule name',
          selector: (item) => item.name,
        },
        {
          label: 'Queue',
          selector: (item) => (
            <AppLink
              path="/conversations"
              params={{
                search: {
                  queue: [item.queue_id],
                  channel: [item.channel],
                },
              }}
            >
              {item.queue_name}
            </AppLink>
          ),
        },
        {
          label: 'Channel',
          selector: (item) => channelAttributes[item.channel].name,
        },
        {
          label: '',
          selector: (item) => (
            <IconButton
              icon="download"
              variant="tertiary"
              onClick={() => handleDownload(item.csv_url)}
            />
          ),
          style: {
            width: 0,
          },
        },
      ]}
      tableStatusProps={{
        loading: isLoading,
        loadingFailed: isError,
        onLoadingRetry: refetch,
        emptyState:
          !batches.length && isSuccess
            ? {
                title: 'No scheduled conversations',
                description:
                  'Click on "Import .csv" above to get started with scheduling conversations.',
                buttonProps: {
                  variant: 'secondary',
                  children: 'Read documentation',
                  onClick: () =>
                    elevioClient.openArticle(
                      articles.settings.scheduledConversations.documentation,
                    ),
                },
              }
            : undefined,
      }}
    />
  );

  return (
    <>
      <Page.Section
        title="All scheduled conversations"
        controls={
          <Button variant="primary" onClick={handleImport}>
            Import .csv
          </Button>
        }
        callout={<ScheduledConversationsBanner />}
      >
        {table}
      </Page.Section>

      <ScheduledConversationsModal />
    </>
  );
};
